import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import Form from '@cloudscape-design/components/form'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Link from '@cloudscape-design/components/link'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Page from '@/components/common/Page'
import { useAccount, useUpdateAccount } from '@/hooks/useAccount'
import { modelSettingsSchema } from '@/lib/schemas'
import type { ModelSettingsFormData } from '@/lib/schemas'
import { MODEL_PROVIDERS, PROVIDER_MAP } from '@/lib/providers'
import styles from './SettingsPage.module.css'

export default function ModelPage() {
  const { account } = useAccount()
  const { updateAccount: updateModelSettings, loading: updatingModelSettings } = useUpdateAccount()

  const savedProvider = account?.modelProvider ?? account?.model_provider ?? ''
  const savedModelId = account?.modelId ?? account?.model_id ?? ''
  const savedApiKey = account?.providerApiKey ?? ''

  const [showKey, setShowKey] = useState(false)

  const { control, handleSubmit, watch, setValue, formState: { errors }, reset } = useForm<ModelSettingsFormData>({
    resolver: zodResolver(modelSettingsSchema),
    defaultValues: { modelProvider: savedProvider, modelId: savedModelId, providerApiKey: savedApiKey },
  })

  useEffect(() => {
    reset({ modelProvider: savedProvider, modelId: savedModelId, providerApiKey: savedApiKey })
    setShowKey(false)
  }, [account, reset])

  const selectedProviderId = watch('modelProvider')
  const selectedProvider = selectedProviderId ? PROVIDER_MAP[selectedProviderId] : undefined
  const selectedModelId = watch('modelId')
  const selectedModel = selectedProvider?.models.find((m) => m.id === selectedModelId)

  const providerOptions = MODEL_PROVIDERS.map((p) => ({ value: p.id, label: p.name }))
  const modelOptions = selectedProvider
    ? selectedProvider.models.map((m) => ({
      value: m.id,
      label: m.name,
      description: `${m.tier} · ${(m.contextWindow / 1000).toFixed(0)}k ctx`,
    }))
    : []

  const handleProviderChange = (providerId: string) => {
    setValue('modelProvider', providerId, { shouldTouch: true, shouldDirty: true })
    setValue('modelId', '', { shouldDirty: true })
    setValue('providerApiKey', '', { shouldDirty: true })
    setShowKey(false)
  }

  const isConfigured = Boolean(savedProvider && savedModelId)

  const handleSubmit_ = (data: ModelSettingsFormData) => {
    updateModelSettings({ modelProvider: data.modelProvider, modelId: data.modelId, providerApiKey: data.providerApiKey || undefined })
  }

  return (
    <Page
      title="Model Settings"
      helpContent={{
        header: 'Model Settings',
        body: (
          <div>
            <p>Configure which AI model powers your agents. Choose providers and models based on your performance and budget needs.</p>
            <h4>Configuration:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li><strong>Model Provider</strong> - Choose provider (OpenAI, Anthropic, etc.)</li>
              <li><strong>Model ID</strong> - Select specific model variant</li>
              <li><strong>API Key</strong> - Provider credentials for authentication</li>
            </ul>
            <h4>Tips:</h4>
            <p>Different models have different costs and capabilities. Experiment with different models to find the best balance for your needs. Update API keys when they expire.</p>
          </div>
        ),
      }}
    >
      <Container>
        <Form>
          <SpaceBetween size="m">

            {!isConfigured && (
              <Alert type="info">
                No model configured. Select a provider and model below — all AI runs will use the system default until you save.
              </Alert>
            )}

            <FormField label="Provider" errorText={errors.modelProvider?.message}>
              <Controller
                name="modelProvider"
                control={control}
                render={({ field: { value } }) => (
                  <Select
                    selectedOption={value ? { value, label: PROVIDER_MAP[value]?.name ?? value } : null}
                    onChange={(e) => handleProviderChange(e.detail.selectedOption.value ?? '')}
                    options={providerOptions}
                    placeholder="Select a provider"
                  />
                )}
              />
            </FormField>

            <FormField label="Model" errorText={errors.modelId?.message}>
              <Controller
                name="modelId"
                control={control}
                render={({ field: { value } }) => (
                  <Select
                    selectedOption={value ? { value, label: selectedProvider?.models.find((m) => m.id === value)?.name ?? value } : null}
                    onChange={(e) => setValue('modelId', e.detail.selectedOption.value ?? '', { shouldTouch: true, shouldDirty: true })}
                    options={modelOptions}
                    placeholder="Select a model"
                    disabled={!selectedProviderId}
                  />
                )}
              />
            </FormField>

            {selectedModel && (
              <Box color="text-body-secondary" fontSize="body-s">
                {selectedModel.tier} · {(selectedModel.contextWindow / 1000).toFixed(0)}k context
              </Box>
            )}

            <FormField
              label="Custom API Key (optional)"
              errorText={errors.providerApiKey?.message}
              constraintText={
                selectedProvider
                  ? <Link href={selectedProvider.apiKeyUrl} external fontSize="body-s">Get API key</Link>
                  : undefined
              }
            >
              <div className={styles.apiKeyRow}>
                <Controller
                  name="providerApiKey"
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Input
                      type={showKey ? 'text' : 'password'}
                      placeholder={selectedProvider?.apiKeyPlaceholder ?? 'No API key saved'}
                      value={value ?? ''}
                      onChange={(e) => onChange(e.detail.value)}
                      onBlur={onBlur}
                    />
                  )}
                />
                <Button
                  variant="icon"
                  ariaLabel={showKey ? 'Hide API key' : 'Show API key'}
                  onClick={() => setShowKey((s) => !s)}
                >
                  {showKey ? 'Hide' : 'Show'}
                </Button>
              </div>
            </FormField>

            <Button
              variant="primary"
              loading={updatingModelSettings}
              onClick={() => { handleSubmit(handleSubmit_)() }}
            >
              Save model settings
            </Button>
          </SpaceBetween>
        </Form>
      </Container>
    </Page>
  )
}
